import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { KINETIC_WORD_SETTING } from './kineticWordSetting';
import { check } from '../skillUsage';

export const wordStrikeOutSkill: CodeoSkill = {
    name: SKILL_NAMES.wordStrikeOut,
    kind: 'codeo',
    description:
        'Cross a word out with one thick rounded bar that shoots across it on a diagonal, ' +
        'overshoots and settles, then retracts into its own head as the beat ends — the rejection ' +
        'as its own beat, for "not X", "no more X", "forget X", the old way being ruled out.',
    checkLooksFor:
        'a rounded bar (border-radius) set at an angle (rotate) and drawn on with a clip-path inset tween',
    documentUsesSkill: check(
        (script, markup, html) =>
            /border-radius|borderRadius|rounded-full/.test(html) &&
            /rotat/.test(html) &&
            /clip-?[Pp]ath/.test(script) &&
            /inset\(/.test(script),
    ),
    reviewNote:
        'A thick bar laid across the word, covering part of its letters, is this technique working: ' +
        'the word is being struck out on purpose.',
    instructions: `# Word Strike-Out

The word arrives and is **struck out**: a single thick bar in the accent colour, with round ends, **shoots across it on a rising diagonal** from its lower-left to its upper-right, overshooting a hair and settling. The word stays readable under it — a rejection has to show what is being rejected — and the bar holds while the voice finishes the thought. As the beat ends the bar **retracts into its own head**, the tail chasing the far end, and the next beat takes over from where the head was.

It differs from \`${SKILL_NAMES.strikethroughReplace}\`, which replaces the word in place. Here the rejection is a beat of its own; what replaces it comes next. Use it on the thing being rejected: "not X", "no more X", "without X", "forget X", "instead of X".

${KINETIC_WORD_SETTING}

## Markup

The word (one line or a stack of two), arriving with \`${SKILL_NAMES.letterMaskRise}\` rather than a fade, then the bar: a pill longer than the copy is wide, rotated about 20° and centred on it, drawn on by its clip.

\`\`\`html
<div id="strike-beat" class="absolute inset-0 flex items-center justify-center">
  <div id="strike-copy" style="position: relative;">
    <div class="st-word" data-l5-allow-literal>SPREAD</div>
    <div class="st-word" data-l5-allow-literal>SHEETS</div>
    <div id="strike-bar" data-l5-absolute style="position: absolute; left: 0; top: 0; border-radius: 999px; background: var(--l5-color-1, #ff5a36); transform-origin: 0 50%;"></div>
  </div>
</div>
\`\`\`

\`\`\`css
.st-word { line-height: 1; white-space: nowrap; font-family: var(--l5-font-0), sans-serif; font-weight: 800; text-transform: uppercase; color: var(--l5-color-3, #fafafa); }
\`\`\`

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const words = [...document.querySelectorAll('.st-word')];
const copy = document.querySelector('#strike-copy');
const bar = document.querySelector('#strike-bar');

// The bar crosses the whole copy at a fixed, steep-enough angle, centred on it, as thick as a
// heavy stroke of the type itself — never derived from the copy's box, which a single wide line
// would turn into a flat hairline. It is laid again after every fit, so it follows the copy when
// the video's font loads.
const ANGLE = -20;                                   // degrees, rising left to right
function placeBar() {
  const W = copy.offsetWidth, H = copy.offsetHeight;
  const fontSize = parseFloat(words[0].style.fontSize);
  const THICK = Math.round(Math.max(fontSize * 0.26, 1080 * 0.045));   // a fat, rounded stroke
  const length = (W * 1.08) / Math.cos(ANGLE * Math.PI / 180);
  gsap.set(bar, {
    width: length, height: THICK,
    x: W / 2 - length / 2, y: H / 2 - THICK / 2,
    rotate: ANGLE, transformOrigin: '50% 50%',
  });
}
words.forEach((w) => fitWord(w, 0.82, placeBar));

const CUE_STRIKE = 0.7, CUE_NEXT = 1.7;
tl.fromTo(bar,
  { clipPath: 'inset(0% 100% 0% 0% round 999px)' },
  { clipPath: 'inset(0% -2% 0% 0% round 999px)', duration: 0.18, ease: 'expo.out' }, CUE_STRIKE - 0.05);
tl.to(bar, { clipPath: 'inset(0% 0% 0% 0% round 999px)', duration: 0.12, ease: 'sine.inOut' }, CUE_STRIKE + 0.13);
// Retract: the tail runs up into the head.
tl.to(bar, { clipPath: 'inset(0% 0% 0% 100% round 999px)', duration: 0.12, ease: 'power2.in' }, CUE_NEXT - 0.12);
tl.set({}, {}, 2.0);
window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Strike the next one too.** Two rejected things in a row ("not this, not that") get the same bar at the same angle on each: the repeat is the point.
- **Hand off from the head.** Where a round carrier mark (\`${SKILL_NAMES.carrierMark}\`) runs through the video, the retracting bar collapses into it at the head and it hops on to the next beat.
- **Fat.** About a quarter of the font-size, and never under ~4.5% of the frame's height, however small the word: thinner reads as a pen line, not a verdict.
- **Steeper for a stack.** A two-line stack can take 25–30°; a single long line stays near 20° so the bar still crosses every letter.

## Pitfalls

- **The word stays readable.** The bar crosses the letters' middles on a diagonal; it never covers a whole line. Check a still with the bar at rest.
- **Not a hairline, not flat.** A thin, near-horizontal line reads as an underline or a typo. The thickness comes from the font-size and the angle is fixed, never from the copy's box.
- **One stroke.** It draws in one fast move, under a fifth of a second. A slow draw reads as a highlighter.
- **Rounded ends.** The \`round\` in the inset keeps the ends rounded while the clip moves; without it the tip is cut square.
- The bar is placed by its transform and carries no CSS \`transform\` of its own beyond the origin.`,
};
